import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  GripVertical, Save, RotateCcw, Settings, Users, Home, TrendingUp,
  MessageSquare, HelpCircle, PlayCircle, FileText, ClipboardCheck,
  Tags, Mail, Palette, UserCog, HardDrive, Shield, Eye, EyeOff, FolderOpen,
  X, Monitor, User, Building2, FileCheck, CreditCard, Ticket, CheckSquare,
  BookTemplate, FileQuestion, Globe, BarChart3, UserPlus, Brain,
  FilePlus, Files, Scale, ChevronDown, ChevronRight, Plus, Trash2, Layers, Bot,
  LayoutDashboard, ListTodo, Share2, Send, Table2, Award
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

// Icon mapping - must include all icons used across Sidebar.js nav items
const iconMap = {
  Home, Users, TrendingUp, MessageSquare, HelpCircle, PlayCircle,
  FileText, ClipboardCheck, Tags, Mail, Palette, UserCog, Settings,
  HardDrive, Shield, GripVertical, FolderOpen, Building2, FileCheck,
  CreditCard, Ticket, CheckSquare, BookTemplate, FileQuestion, Globe,
  BarChart3, UserPlus, Brain, FilePlus, Files, Scale, Layers, Bot,
  LayoutDashboard, ListTodo, Share2, Send, Table2, Award
};

// Default admin sidebar links (Super Admin) - must match mainAdminNavItems in Sidebar.js
const defaultAdminLinks = [
  { id: 'dashboard', path: '/admin', icon: 'Home', label: 'Dashboard', visible: true },
  { id: 'companies', path: '/admin/companies', icon: 'Building2', label: 'Companies', visible: true },
  { id: 'applications', path: '/admin/applications', icon: 'FileCheck', label: 'Applications', visible: true },
  { id: 'membership-plans', path: '/admin/membership-plans', icon: 'CreditCard', label: 'Membership Plans', visible: true },
  { id: 'coupons', path: '/admin/coupons', icon: 'Ticket', label: 'Coupon Codes', visible: true },
  { id: 'company-approval', path: '/admin/company-approval-steps', icon: 'ClipboardCheck', label: 'Approval Setup', visible: true },
  { id: 'company-verifications', path: '/admin/company-verifications', icon: 'CheckSquare', label: 'Verifications', visible: true },
  { id: 'platform-analytics', path: '/admin/platform-analytics', icon: 'TrendingUp', label: 'Platform Analytics', visible: true },
  { id: 'email-leads', path: '/admin/leads', icon: 'Mail', label: 'Email Leads', visible: true },
  { id: 'trados-agent', path: '/admin/trados-agent', icon: 'Bot', label: 'Trados Agent', visible: true },
  { id: 'users', path: '/admin/users', icon: 'Users', label: 'Manage Users', visible: true },
  { id: 'tier-approvals', path: '/admin/tier-approvals', icon: 'ClipboardCheck', label: 'Tier Approvals', visible: true },
  { id: 'tier-questions', path: '/admin/tier-questions', icon: 'Shield', label: 'Tier Questions', visible: true },
  { id: 'account-types', path: '/admin/account-types', icon: 'Tags', label: 'Account Types', visible: true },
  { id: 'deals', path: '/admin/deals', icon: 'TrendingUp', label: 'Opportunities', visible: true },
  { id: 'announcements', path: '/admin/announcements', icon: 'MessageSquare', label: 'Announcements', visible: true },
  { id: 'support', path: '/admin/support', icon: 'HelpCircle', label: 'Support', visible: true },
  { id: 'videos', path: '/admin/videos', icon: 'PlayCircle', label: 'Videos', visible: true },
  { id: 'quote-requests', path: '/admin/quote-requests', icon: 'FileText', label: 'Quote Requests', visible: true },
  { id: 'process-steps', path: '/admin/process-steps', icon: 'ClipboardCheck', label: 'Quote Steps', visible: true },
  { id: 'process-templates', path: '/admin/process-templates', icon: 'BookTemplate', label: 'Steps Admin MGMT', visible: true },
  { id: 'question-templates', path: '/admin/question-templates', icon: 'FileQuestion', label: 'Questions Admin MGMT', visible: true },
  { id: 'company-questions', path: '/admin/company-questions', icon: 'UserCog', label: 'Quote Questions', visible: true },
  { id: 'create-loi', path: '/create-loi', icon: 'FilePlus', label: 'Create LOI', visible: true },
  { id: 'my-created-lois', path: '/admin/my-created-lois', icon: 'Files', label: 'Created LOIs', visible: true },
  { id: 'all-created-lois', path: '/admin/created-lois', icon: 'Files', label: 'Created LOIs via Trados', visible: true },
  { id: 'verify-loi', path: '/admin/verify-loi-internal', icon: 'FileCheck', label: 'Verify LOIs', visible: true },
  { id: 'my-verified-lois', path: '/admin/my-verified-lois', icon: 'FileCheck', label: 'All LOIs Verifications', visible: true },
  { id: 'documents', path: '/admin/documents', icon: 'FolderOpen', label: 'Documents', visible: true },
  { id: 'logs', path: '/admin/logs', icon: 'Mail', label: 'Logs', visible: true },
  { id: 'upload-settings', path: '/admin/upload-settings', icon: 'HardDrive', label: 'Upload Settings', visible: true },
  { id: 'theme', path: '/admin/theme', icon: 'Palette', label: 'Theme', visible: true },
  { id: 'theme-presets', path: '/admin/theme-presets', icon: 'Palette', label: 'Theme Presets', visible: true },
  { id: 'public-theme', path: '/admin/public-theme', icon: 'Palette', label: 'Public Theme', visible: true },
  { id: 'settings', path: '/admin/settings', icon: 'Settings', label: 'Settings', visible: true },
  { id: 'security-settings', path: '/admin/security-settings', icon: 'Shield', label: 'Security', visible: true },
  { id: 'stripe-settings', path: '/admin/stripe-settings', icon: 'CreditCard', label: 'Stripe', visible: true },
  { id: 'membership-settings', path: '/admin/membership-settings', icon: 'Shield', label: 'Membership Settings', visible: true },
  { id: 'financial', path: '/admin/financial', icon: 'BarChart3', label: 'Financial', visible: true },
  { id: 'commodities', path: '/admin/commodities', icon: 'TrendingUp', label: 'Commodity Tracker', visible: true },
  { id: 'directory-public', path: '/directory', icon: 'Globe', label: 'Company Directory', visible: true },
  { id: 'directory', path: '/admin/directory', icon: 'Globe', label: 'Manage Directory', visible: true },
  { id: 'directory-data', path: '/admin/cd', icon: 'Table2', label: 'Directory Data', visible: true },
  { id: 'integration-settings', path: '/admin/integration-settings', icon: 'Globe', label: 'API & Integrations', visible: true },
  { id: 'analytics-scripts', path: '/admin/analytics', icon: 'BarChart3', label: 'Analytics', visible: true },
  { id: 'referral-tree', path: '/admin/referral-tree', icon: 'Users', label: 'Referrals', visible: true },
  { id: 'global-users', path: '/admin/global-users', icon: 'Globe', label: 'Global Users', visible: true },
  { id: 'my-tasks', path: '/admin/my-tasks', icon: 'ListTodo', label: 'My Tasks', visible: true },
  { id: 'invite-users', path: '/admin/invite', icon: 'UserPlus', label: 'Invite', visible: true },
  { id: 'share-selling-process', path: '/admin/share-selling-process', icon: 'Share2', label: 'Share Process', visible: true },
  { id: 'external-services', path: '/admin/external-services', icon: 'Globe', label: 'External Services', visible: true },
  { id: 'intel-settings', path: '/admin/intel-settings', icon: 'Brain', label: 'Intel Settings', visible: true },
  { id: 'email-templates', path: '/admin/email-templates', icon: 'Mail', label: 'Email Templates', visible: true },
  { id: 'newsletters', path: '/admin/newsletters', icon: 'Send', label: 'Newsletters', visible: true },
  { id: 'posting', path: '/admin/posting', icon: 'FilePlus', label: 'Posting', visible: true },
  { id: 'disclaimer', path: '/admin/disclaimer', icon: 'Scale', label: 'Legal Disclaimer', visible: true },
  { id: 'dashboard-control', path: '/admin/dashboard-control', icon: 'LayoutDashboard', label: 'Dashboard Control', visible: true },
];

// Default company admin sidebar links - must match companyAdminNavItems in Sidebar.js
const defaultCompanyAdminLinks = [
  { id: 'dashboard', path: '/admin', icon: 'Home', label: 'Dashboard', visible: true },
  { id: 'company-settings', path: '/admin/company-settings', icon: 'Building2', label: 'Company Settings', visible: true },
  { id: 'edit-business-info', path: '/admin/business-info', icon: 'Building2', label: 'Edit Business Info', visible: true },
  { id: 'network-badge', path: '/admin/network-badge', icon: 'Award', label: 'Trados Network Badge', visible: true },
  { id: 'billing', path: '/admin/billing', icon: 'CreditCard', label: 'Billing', visible: true },
  { id: 'users', path: '/admin/users', icon: 'Users', label: 'Manage Users', visible: true },
  { id: 'account-types', path: '/admin/account-types', icon: 'Tags', label: 'Account Types', visible: true },
  { id: 'directory', path: '/directory', icon: 'Globe', label: 'Company Directory', visible: true },
  { id: 'deals', path: '/admin/deals', icon: 'TrendingUp', label: 'Opportunities', visible: true },
  { id: 'announcements', path: '/admin/announcements', icon: 'MessageSquare', label: 'Announcements', visible: true },
  { id: 'support', path: '/admin/support', icon: 'HelpCircle', label: 'Support', visible: true },
  { id: 'videos', path: '/admin/videos', icon: 'PlayCircle', label: 'Videos', visible: true },
  { id: 'company-questions', path: '/admin/company-questions', icon: 'FileQuestion', label: 'Quote Question MGMT', visible: true },
  { id: 'quote-requests', path: '/admin/quote-requests', icon: 'FileText', label: 'View Quote Request', visible: true },
  { id: 'process-steps', path: '/admin/process-steps', icon: 'ClipboardCheck', label: 'Quote Steps', visible: true },
  { id: 'create-loi', path: '/create-loi', icon: 'FilePlus', label: 'Create LOI', visible: true },
  { id: 'my-created-lois', path: '/admin/my-created-lois', icon: 'Files', label: 'Created LOIs', visible: true },
  { id: 'verify-loi', path: '/admin/verify-loi-internal', icon: 'FileCheck', label: 'Verify LOIs', visible: true },
  { id: 'my-verified-lois', path: '/admin/my-verified-lois', icon: 'FileCheck', label: 'Verified LOIs', visible: true },
  { id: 'access-requests', path: '/admin/access-requests', icon: 'Globe', label: 'Access Requests', visible: true },
  { id: 'my-tasks', path: '/admin/my-tasks', icon: 'ListTodo', label: 'My Tasks', visible: true },
  { id: 'invite-users', path: '/admin/invite', icon: 'UserPlus', label: 'Invite Users', visible: true },
  { id: 'share-selling-process', path: '/admin/share-selling-process', icon: 'Share2', label: 'Share Selling Process', visible: true },
];

// Default user sidebar links - must match defaultUserNavItems in Sidebar.js
const defaultUserLinks = [
  { id: 'dashboard', path: '/admin/dashboard', icon: 'Home', label: 'Dashboard', visible: true },
  { id: 'request-quote', path: '/admin/requests/new', icon: 'FileText', label: 'Request a Quote', visible: true },
  { id: 'my-requests', path: '/admin/requests', icon: 'ClipboardCheck', label: 'View My Requests', visible: true },
  { id: 'create-loi', path: '/create-loi', icon: 'FilePlus', label: 'Create LOI', visible: true },
  { id: 'my-created-lois', path: '/admin/my-created-lois', icon: 'Files', label: 'My Created LOIs', visible: true },
  { id: 'edit-business-info', path: '/admin/business-info', icon: 'Building2', label: 'Edit Business Info', visible: true },
  { id: 'network-badge', path: '/admin/network-badge', icon: 'Award', label: 'Trados Network Badge', visible: true },
  { id: 'verify-loi', path: '/admin/verify-loi-internal', icon: 'FileCheck', label: 'Verify LOIs', visible: true },
  { id: 'my-verified-lois', path: '/admin/my-verified-lois', icon: 'FileCheck', label: 'My Verified LOIs', visible: true },
  { id: 'directory', path: '/directory', icon: 'Globe', label: 'Company Directory', visible: true },
  { id: 'my-company-listing', path: '/admin/my-company-listing', icon: 'Building2', label: 'My Company Listing', visible: true },
  { id: 'opportunities', path: '/admin/deals', icon: 'TrendingUp', label: 'Opportunities', visible: true },
  { id: 'announcements', path: '/admin/announcements', icon: 'MessageSquare', label: 'Announcements', visible: true },
  { id: 'support', path: '/admin/support', icon: 'HelpCircle', label: 'Support Ticket', visible: true },
  { id: 'videos', path: '/admin/videos', icon: 'PlayCircle', label: 'Educational Videos', visible: true },
  { id: 'my-referrals', path: '/admin/my-referrals', icon: 'Users', label: 'My Referrals', visible: true },
  { id: 'my-tasks', path: '/admin/my-tasks', icon: 'ListTodo', label: 'My Tasks', visible: true },
  { id: 'invite-users', path: '/admin/invite', icon: 'UserPlus', label: 'Invite Users', visible: true },
  { id: 'share-selling-process', path: '/admin/share-selling-process', icon: 'Share2', label: 'Share Selling Process', visible: true },
];

const AdminSidebarSettings = () => {
  const [adminLinks, setAdminLinks] = useState(defaultAdminLinks);
  const [companyAdminLinks, setCompanyAdminLinks] = useState(defaultCompanyAdminLinks);
  const [userLinks, setUserLinks] = useState(defaultUserLinks);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('admin');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const { userTheme, adminTheme } = useTheme();

  useEffect(() => {
    fetchSidebarOrder();
  }, []);

  const fetchSidebarOrder = async () => {
    try {
      const response = await api.get('/admin/settings/sidebar-order');
      if (response.data.admin_links?.length > 0) {
        // Merge saved order with defaults (in case new links were added)
        const savedAdminIds = response.data.admin_links.map(l => l.id);
        const mergedAdmin = [
          ...response.data.admin_links,
          ...defaultAdminLinks.filter(l => !savedAdminIds.includes(l.id))
        ];
        setAdminLinks(mergedAdmin);
      }
      if (response.data.company_admin_links?.length > 0) {
        const savedCompanyAdminIds = response.data.company_admin_links.map(l => l.id);
        const mergedCompanyAdmin = [
          ...response.data.company_admin_links,
          ...defaultCompanyAdminLinks.filter(l => !savedCompanyAdminIds.includes(l.id))
        ];
        setCompanyAdminLinks(mergedCompanyAdmin);
      }
      if (response.data.user_links?.length > 0) {
        const savedUserIds = response.data.user_links.map(l => l.id);
        const mergedUser = [
          ...response.data.user_links,
          ...defaultUserLinks.filter(l => !savedUserIds.includes(l.id))
        ];
        setUserLinks(mergedUser);
      }
    } catch (error) {
      console.error('Failed to fetch sidebar order:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSidebarOrder = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings/sidebar-order', {
        admin_links: adminLinks,
        company_admin_links: companyAdminLinks,
        user_links: userLinks
      });
      toast.success('Sidebar order saved! Changes will appear on next page refresh.');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save sidebar order');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    if (activeTab === 'admin') {
      setAdminLinks(defaultAdminLinks);
    } else if (activeTab === 'company-admin') {
      setCompanyAdminLinks(defaultCompanyAdminLinks);
    } else {
      setUserLinks(defaultUserLinks);
    }
    setHasChanges(true);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;

    // For admin tabs with categories, handle cross-droppable moves
    if (isAdminTab && source.droppableId !== destination.droppableId) {
      const setter = activeTab === 'admin' ? setAdminLinks : setCompanyAdminLinks;
      const sourceDropId = source.droppableId; // 'ungrouped' or 'cat-xxx'
      const destDropId = destination.droppableId;

      setter(prev => {
        const links = Array.from(prev);
        // Find the items in the source droppable
        const sourceItems = sourceDropId === 'ungrouped'
          ? links.filter(item => item.type !== 'parent' && !item.parent_id)
          : links.filter(item => item.parent_id === sourceDropId);
        const movedItem = sourceItems[source.index];
        if (!movedItem) return prev;

        // Update parent_id
        const newParentId = destDropId === 'ungrouped' ? undefined : destDropId;
        const updatedLinks = links.map(item =>
          item.id === movedItem.id ? { ...item, parent_id: newParentId } : item
        );
        return updatedLinks;
      });
      setHasChanges(true);
      return;
    }

    // Same droppable reordering
    if (isAdminTab) {
      const droppableId = source.droppableId;
      const setter = activeTab === 'admin' ? setAdminLinks : setCompanyAdminLinks;
      setter(prev => {
        const links = Array.from(prev);
        // Get items in this droppable
        const droppableItems = droppableId === 'ungrouped'
          ? links.filter(item => item.type !== 'parent' && !item.parent_id)
          : droppableId === 'categories'
          ? links.filter(item => item.type === 'parent')
          : links.filter(item => item.parent_id === droppableId);

        const [removed] = droppableItems.splice(source.index, 1);
        droppableItems.splice(destination.index, 0, removed);

        // Rebuild full array preserving order of other items
        const droppableIds = new Set(droppableItems.map(i => i.id));
        const otherItems = links.filter(item => !droppableIds.has(item.id));

        // Re-insert droppable items in their new order at the position of the first original item
        const firstOrigIdx = links.findIndex(item => droppableIds.has(item.id));
        const result = [...otherItems];
        result.splice(Math.min(firstOrigIdx, result.length), 0, ...droppableItems);
        return result;
      });
      setHasChanges(true);
      return;
    }

    // User tab - simple flat reorder
    const newLinks = Array.from(userLinks);
    const [removed] = newLinks.splice(source.index, 1);
    newLinks.splice(destination.index, 0, removed);
    setUserLinks(newLinks);
    setHasChanges(true);
  };

  const toggleVisibility = (linkId) => {
    if (activeTab === 'admin') {
      setAdminLinks(prev => prev.map(link => 
        link.id === linkId ? { ...link, visible: !link.visible } : link
      ));
    } else if (activeTab === 'company-admin') {
      setCompanyAdminLinks(prev => prev.map(link => 
        link.id === linkId ? { ...link, visible: !link.visible } : link
      ));
    } else {
      setUserLinks(prev => prev.map(link => 
        link.id === linkId ? { ...link, visible: !link.visible } : link
      ));
    }
    setHasChanges(true);
  };

  const getCurrentLinks = () => {
    if (activeTab === 'admin') return adminLinks;
    if (activeTab === 'company-admin') return companyAdminLinks;
    return userLinks;
  };
  
  const currentLinks = getCurrentLinks();

  const startEditing = (linkId, currentLabel) => {
    setEditingLinkId(linkId);
    setEditingLabel(currentLabel);
  };

  const saveLabel = () => {
    if (!editingLinkId || !editingLabel.trim()) {
      setEditingLinkId(null);
      return;
    }

    if (activeTab === 'admin') {
      setAdminLinks(prev => prev.map(link => 
        link.id === editingLinkId ? { ...link, label: editingLabel.trim() } : link
      ));
    } else if (activeTab === 'company-admin') {
      setCompanyAdminLinks(prev => prev.map(link => 
        link.id === editingLinkId ? { ...link, label: editingLabel.trim() } : link
      ));
    } else {
      setUserLinks(prev => prev.map(link => 
        link.id === editingLinkId ? { ...link, label: editingLabel.trim() } : link
      ));
    }
    
    setEditingLinkId(null);
    setEditingLabel('');
    setHasChanges(true);
  };

  const handleLabelKeyDown = (e) => {
    if (e.key === 'Enter') {
      saveLabel();
    } else if (e.key === 'Escape') {
      setEditingLinkId(null);
      setEditingLabel('');
    }
  };

  // --- Category management (admin & company-admin only) ---
  const isAdminTab = activeTab === 'admin' || activeTab === 'company-admin';

  const getCategories = () => {
    return getCurrentLinks().filter(item => item.type === 'parent');
  };

  const addCategory = () => {
    if (!newCategoryName.trim()) return;
    const catId = `cat-${Date.now()}`;
    const newCat = {
      id: catId,
      type: 'parent',
      icon: 'Layers',
      label: newCategoryName.trim(),
      visible: true
    };
    const setter = activeTab === 'admin' ? setAdminLinks : setCompanyAdminLinks;
    setter(prev => [...prev, newCat]);
    setNewCategoryName('');
    setShowAddCategory(false);
    setHasChanges(true);
  };

  const deleteCategory = (catId) => {
    const setter = activeTab === 'admin' ? setAdminLinks : setCompanyAdminLinks;
    setter(prev => {
      // Ungroup children (remove parent_id), then remove the category
      return prev
        .map(item => item.parent_id === catId ? { ...item, parent_id: undefined } : item)
        .filter(item => item.id !== catId);
    });
    setHasChanges(true);
  };

  const assignToCategory = (linkId, parentId) => {
    const setter = activeTab === 'admin' ? setAdminLinks : setCompanyAdminLinks;
    setter(prev => prev.map(item =>
      item.id === linkId
        ? { ...item, parent_id: parentId || undefined }
        : item
    ));
    setHasChanges(true);
  };

  const toggleCategoryExpanded = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  // Build grouped structure for display
  const getGroupedLinks = () => {
    const links = getCurrentLinks();
    const categories = links.filter(item => item.type === 'parent');
    const ungrouped = links.filter(item => item.type !== 'parent' && !item.parent_id);
    const grouped = {};
    categories.forEach(cat => {
      grouped[cat.id] = links.filter(item => item.parent_id === cat.id);
    });
    return { categories, ungrouped, grouped };
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800 mb-1">Sidebar Link Management</h1>
            <p className="text-gray-600">Drag and drop to reorder sidebar navigation links</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={resetToDefault}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition"
            >
              <RotateCcw className="w-4 h-4" />
              Reset to Default
            </button>
            <button
              onClick={saveSidebarOrder}
              disabled={!hasChanges || saving}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition disabled:opacity-50"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Order'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('admin')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'admin' 
                ? 'text-white' 
                : 'text-gray-600 bg-white border border-gray-200 hover:bg-gray-50'
            }`}
            style={activeTab === 'admin' ? { backgroundColor: BRAND_BLUE } : {}}
          >
            Super Admin Sidebar
          </button>
          <button
            onClick={() => setActiveTab('company-admin')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'company-admin' 
                ? 'text-white' 
                : 'text-gray-600 bg-white border border-gray-200 hover:bg-gray-50'
            }`}
            style={activeTab === 'company-admin' ? { backgroundColor: BRAND_BLUE } : {}}
          >
            Company Admin Sidebar
          </button>
          <button
            onClick={() => setActiveTab('user')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'user' 
                ? 'text-white' 
                : 'text-gray-600 bg-white border border-gray-200 hover:bg-gray-50'
            }`}
            style={activeTab === 'user' ? { backgroundColor: BRAND_BLUE } : {}}
          >
            User Sidebar
          </button>
        </div>

        {/* Info Box */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-blue-800 text-sm">
            <strong>Tip:</strong> Drag the grip icon to reorder links. Click the eye icon to show/hide links. Click a label to rename it.
            {activeTab === 'admin'
              ? ' Create categories to group links into collapsible sections. Super Admin sidebar changes affect all super administrators.'
              : activeTab === 'company-admin'
              ? ' Create categories to group links into collapsible sections. Company Admin sidebar changes affect all company administrators.'
              : ' User sidebar changes affect all regular users.'}
          </p>
        </div>

        {/* Category Management (admin tabs only) */}
        {isAdminTab && (
          <div className="mb-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-gray-500" />
                <h2 className="text-lg font-medium text-gray-800">Link Categories</h2>
              </div>
              <button
                onClick={() => setShowAddCategory(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white rounded-lg transition hover:opacity-90"
                style={{ backgroundColor: BRAND_BLUE }}
              >
                <Plus className="w-4 h-4" />
                Add Category
              </button>
            </div>
            <div className="p-4">
              {showAddCategory && (
                <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Layers className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') addCategory();
                      if (e.key === 'Escape') { setShowAddCategory(false); setNewCategoryName(''); }
                    }}
                    placeholder="Category name (e.g. Settings, Content, LOI)"
                    autoFocus
                    className="flex-1 px-3 py-1.5 text-sm border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={addCategory}
                    disabled={!newCategoryName.trim()}
                    className="px-3 py-1.5 text-sm text-white rounded-lg disabled:opacity-50"
                    style={{ backgroundColor: BRAND_BLUE }}
                  >
                    Create
                  </button>
                  <button
                    onClick={() => { setShowAddCategory(false); setNewCategoryName(''); }}
                    className="p-1.5 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              {getCategories().length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No categories yet. Create categories to organize your sidebar links into collapsible groups.
                </p>
              ) : (
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="categories">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                        {getCategories().map((cat, index) => (
                          <Draggable key={cat.id} draggableId={cat.id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
                                  snapshot.isDragging ? 'bg-blue-50 border-blue-200 shadow-lg' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                } ${!cat.visible ? 'opacity-50' : ''}`}
                              >
                                <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing">
                                  <GripVertical className="w-4 h-4 text-gray-400" />
                                </div>
                                <Layers className="w-5 h-5" style={{ color: BRAND_BLUE }} />
                                {editingLinkId === cat.id ? (
                                  <input
                                    type="text"
                                    value={editingLabel}
                                    onChange={(e) => setEditingLabel(e.target.value)}
                                    onBlur={saveLabel}
                                    onKeyDown={handleLabelKeyDown}
                                    autoFocus
                                    className="flex-1 px-2 py-1 text-sm font-medium border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                ) : (
                                  <span
                                    className="flex-1 font-medium text-gray-800 text-sm cursor-pointer hover:text-blue-600"
                                    onClick={() => startEditing(cat.id, cat.label)}
                                    title="Click to rename"
                                  >
                                    {cat.label}
                                  </span>
                                )}
                                <span className="text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full">
                                  {getCurrentLinks().filter(l => l.parent_id === cat.id).length} links
                                </span>
                                <button
                                  onClick={() => toggleVisibility(cat.id)}
                                  className={`p-1.5 rounded-lg transition ${cat.visible ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                                  title={cat.visible ? 'Click to hide' : 'Click to show'}
                                >
                                  {cat.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                </button>
                                <button
                                  onClick={() => deleteCategory(cat.id)}
                                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                  title="Delete category (links will become ungrouped)"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>
          </div>
        )}

        {/* Links List */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-800">
              {activeTab === 'admin'
                ? 'Super Admin Navigation Links'
                : activeTab === 'company-admin'
                ? 'Company Admin Navigation Links'
                : 'User Navigation Links'}
            </h2>
          </div>

          {isAdminTab ? (
            /* Grouped view for admin tabs */
            (() => {
              const { categories, ungrouped, grouped } = getGroupedLinks();
              return (
                <DragDropContext onDragEnd={onDragEnd}>
                  {/* Ungrouped Links */}
                  <div className="border-b border-gray-200">
                    <div className="px-6 py-3 bg-gray-50/50 flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-500">Ungrouped Links</span>
                      <span className="text-xs text-gray-400">({ungrouped.length})</span>
                    </div>
                    <Droppable droppableId="ungrouped">
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`divide-y divide-gray-100 min-h-[4px] ${snapshot.isDraggingOver ? 'bg-blue-50/30' : ''}`}
                        >
                          {ungrouped.map((link, index) => {
                            const IconComponent = iconMap[link.icon] || FileText;
                            return (
                              <Draggable key={link.id} draggableId={link.id} index={index}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className={`flex items-center gap-4 px-6 py-3 transition ${
                                      snapshot.isDragging ? 'bg-blue-50 shadow-lg' : 'hover:bg-gray-50'
                                    } ${!link.visible ? 'opacity-50' : ''}`}
                                  >
                                    <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing">
                                      <GripVertical className="w-5 h-5 text-gray-400" />
                                    </div>
                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${BRAND_BLUE}15` }}>
                                      <IconComponent className="w-4 h-4" style={{ color: BRAND_BLUE }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      {editingLinkId === link.id ? (
                                        <input type="text" value={editingLabel} onChange={(e) => setEditingLabel(e.target.value)} onBlur={saveLabel} onKeyDown={handleLabelKeyDown} autoFocus className="w-full px-2 py-1 font-medium text-gray-800 border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                      ) : (
                                        <p className="font-medium text-gray-800 text-sm cursor-pointer hover:text-blue-600 hover:underline" onClick={() => startEditing(link.id, link.label)} title="Click to rename">{link.label}</p>
                                      )}
                                      <p className="text-xs text-gray-400 truncate">{link.path}</p>
                                    </div>
                                    {/* Category Assignment */}
                                    <select
                                      value={link.parent_id || ''}
                                      onChange={(e) => assignToCategory(link.id, e.target.value)}
                                      className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-[140px]"
                                      title="Assign to category"
                                    >
                                      <option value="">No category</option>
                                      {getCategories().map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                                      ))}
                                    </select>
                                    <button onClick={() => toggleVisibility(link.id)} className={`p-1.5 rounded-lg transition ${link.visible ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`} title={link.visible ? 'Click to hide' : 'Click to show'}>
                                      {link.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                    </button>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>

                  {/* Category Groups */}
                  {categories.map((cat) => {
                    const children = grouped[cat.id] || [];
                    const isExpanded = expandedCategories[cat.id] !== false; // default expanded
                    return (
                      <div key={cat.id} className="border-b border-gray-200">
                        <div
                          className="px-6 py-3 bg-gradient-to-r from-blue-50 to-gray-50 flex items-center gap-2 cursor-pointer hover:from-blue-100"
                          onClick={() => toggleCategoryExpanded(cat.id)}
                        >
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                          <Layers className="w-4 h-4" style={{ color: BRAND_BLUE }} />
                          <span className="text-sm font-medium" style={{ color: BRAND_BLUE }}>{cat.label}</span>
                          <span className="text-xs text-gray-400">({children.length} links)</span>
                        </div>
                        {isExpanded && (
                          <Droppable droppableId={cat.id}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className={`divide-y divide-gray-100 min-h-[4px] ${snapshot.isDraggingOver ? 'bg-blue-50/30' : ''}`}
                              >
                                {children.length === 0 && (
                                  <div className="px-6 py-4 text-center text-sm text-gray-400">
                                    Drag links here or use the category dropdown to assign links
                                  </div>
                                )}
                                {children.map((link, index) => {
                                  const IconComponent = iconMap[link.icon] || FileText;
                                  return (
                                    <Draggable key={link.id} draggableId={link.id} index={index}>
                                      {(provided, snapshot) => (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          className={`flex items-center gap-4 px-6 pl-10 py-3 transition ${
                                            snapshot.isDragging ? 'bg-blue-50 shadow-lg' : 'hover:bg-gray-50'
                                          } ${!link.visible ? 'opacity-50' : ''}`}
                                        >
                                          <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing">
                                            <GripVertical className="w-5 h-5 text-gray-400" />
                                          </div>
                                          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${BRAND_BLUE}15` }}>
                                            <IconComponent className="w-4 h-4" style={{ color: BRAND_BLUE }} />
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            {editingLinkId === link.id ? (
                                              <input type="text" value={editingLabel} onChange={(e) => setEditingLabel(e.target.value)} onBlur={saveLabel} onKeyDown={handleLabelKeyDown} autoFocus className="w-full px-2 py-1 font-medium text-gray-800 border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                            ) : (
                                              <p className="font-medium text-gray-800 text-sm cursor-pointer hover:text-blue-600 hover:underline" onClick={() => startEditing(link.id, link.label)} title="Click to rename">{link.label}</p>
                                            )}
                                            <p className="text-xs text-gray-400 truncate">{link.path}</p>
                                          </div>
                                          <select
                                            value={link.parent_id || ''}
                                            onChange={(e) => assignToCategory(link.id, e.target.value)}
                                            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-[140px]"
                                            title="Assign to category"
                                          >
                                            <option value="">No category</option>
                                            {getCategories().map(c => (
                                              <option key={c.id} value={c.id}>{c.label}</option>
                                            ))}
                                          </select>
                                          <button onClick={() => toggleVisibility(link.id)} className={`p-1.5 rounded-lg transition ${link.visible ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`} title={link.visible ? 'Click to hide' : 'Click to show'}>
                                            {link.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                          </button>
                                        </div>
                                      )}
                                    </Draggable>
                                  );
                                })}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        )}
                      </div>
                    );
                  })}
                </DragDropContext>
              );
            })()
          ) : (
            /* Flat view for user tab */
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="sidebar-links">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="divide-y divide-gray-100"
                  >
                    {currentLinks.map((link, index) => {
                      const IconComponent = iconMap[link.icon] || FileText;
                      return (
                        <Draggable key={link.id} draggableId={link.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`flex items-center gap-4 px-6 py-4 transition ${
                                snapshot.isDragging ? 'bg-blue-50 shadow-lg' : 'hover:bg-gray-50'
                              } ${!link.visible ? 'opacity-50' : ''}`}
                            >
                              <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing">
                                <GripVertical className="w-5 h-5 text-gray-400" />
                              </div>
                              <span className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600">{index + 1}</span>
                              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${BRAND_BLUE}15` }}>
                                <IconComponent className="w-5 h-5" style={{ color: BRAND_BLUE }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                {editingLinkId === link.id ? (
                                  <input type="text" value={editingLabel} onChange={(e) => setEditingLabel(e.target.value)} onBlur={saveLabel} onKeyDown={handleLabelKeyDown} autoFocus className="w-full px-2 py-1 font-medium text-gray-800 border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                ) : (
                                  <p className="font-medium text-gray-800 cursor-pointer hover:text-blue-600 hover:underline" onClick={() => startEditing(link.id, link.label)} title="Click to rename">{link.label}</p>
                                )}
                                <p className="text-sm text-gray-500 truncate">{link.path}</p>
                              </div>
                              <button onClick={() => toggleVisibility(link.id)} className={`p-2 rounded-lg transition ${link.visible ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`} title={link.visible ? 'Click to hide' : 'Click to show'}>
                                {link.visible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                              </button>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>

        {/* Preview */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-800">Preview (Visible Links Only)</h3>
            <button
              onClick={() => setShowPreviewModal(true)}
              data-testid="preview-sidebar-btn"
              className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition hover:opacity-90"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              <Monitor className="w-4 h-4" />
              Full Preview
            </button>
          </div>
          <div
            className="rounded-xl p-4 max-w-xs"
            style={{ backgroundColor: BRAND_BLUE }}
          >
            <div className="space-y-1">
              {isAdminTab ? (() => {
                const { categories, ungrouped, grouped } = getGroupedLinks();
                return (
                  <>
                    {ungrouped.filter(l => l.visible).map((link) => {
                      const IconComponent = iconMap[link.icon] || FileText;
                      return (
                        <div key={link.id} className="flex items-center gap-3 px-3 py-2 rounded-lg text-white/80">
                          <IconComponent className="w-4 h-4" />
                          <span className="text-sm">{link.label}</span>
                        </div>
                      );
                    })}
                    {categories.filter(c => c.visible).map((cat) => {
                      const children = (grouped[cat.id] || []).filter(l => l.visible);
                      return (
                        <div key={cat.id}>
                          <div className="flex items-center gap-2 px-3 py-2 text-white/50 mt-2">
                            <ChevronDown className="w-3 h-3" />
                            <span className="text-xs font-semibold uppercase tracking-wider">{cat.label}</span>
                          </div>
                          {children.map((link) => {
                            const IconComponent = iconMap[link.icon] || FileText;
                            return (
                              <div key={link.id} className="flex items-center gap-3 px-3 pl-6 py-2 rounded-lg text-white/80">
                                <IconComponent className="w-4 h-4" />
                                <span className="text-sm">{link.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </>
                );
              })() : (
                currentLinks.filter(l => l.visible).map((link) => {
                  const IconComponent = iconMap[link.icon] || FileText;
                  return (
                    <div key={link.id} className="flex items-center gap-3 px-3 py-2 rounded-lg text-white/80">
                      <IconComponent className="w-4 h-4" />
                      <span className="text-sm">{link.label}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Preview Modal */}
        {showPreviewModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Sidebar Preview</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {activeTab === 'admin' ? 'How admins will see the sidebar' : 'How users will see the sidebar'}
                  </p>
                </div>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Sidebar Preview */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    {activeTab === 'admin' ? <Shield className="w-4 h-4 text-gray-500" /> : <User className="w-4 h-4 text-gray-500" />}
                    <span className="text-sm font-medium text-gray-600">
                      {activeTab === 'admin' ? 'Admin View' : 'User View'}
                    </span>
                  </div>
                  <div 
                    className="rounded-xl overflow-hidden shadow-lg"
                    style={{ 
                      width: '280px',
                      backgroundColor: activeTab === 'admin' 
                        ? (adminTheme?.sidebar?.backgroundColor || BRAND_BLUE)
                        : (userTheme?.sidebar?.backgroundColor || BRAND_BLUE)
                    }}
                  >
                    {/* Logo Area */}
                    <div className="p-4 border-b border-white/10">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-white font-semibold text-sm">Trados</p>
                          <p className="text-white/60 text-xs">Trade Platform</p>
                        </div>
                      </div>
                    </div>

                    {/* Navigation Links */}
                    <div className="p-3 space-y-1 max-h-[400px] overflow-y-auto">
                      {isAdminTab ? (() => {
                        const { categories, ungrouped, grouped } = getGroupedLinks();
                        const activeColor = activeTab === 'admin'
                          ? (adminTheme?.sidebar?.activeColor || '#1E3A52')
                          : (userTheme?.sidebar?.activeColor || '#1E3A52');
                        let isFirst = true;
                        const renderLink = (link) => {
                          const IconComponent = iconMap[link.icon] || FileText;
                          const isActive = isFirst;
                          if (isFirst) isFirst = false;
                          return (
                            <div key={link.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors" style={{ backgroundColor: isActive ? activeColor : 'transparent', color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.75)' }}>
                              <IconComponent className="w-4 h-4 flex-shrink-0" />
                              <span className="text-sm font-medium">{link.label}</span>
                            </div>
                          );
                        };
                        return (
                          <>
                            {ungrouped.filter(l => l.visible).map(renderLink)}
                            {categories.filter(c => c.visible).map((cat) => {
                              const children = (grouped[cat.id] || []).filter(l => l.visible);
                              return (
                                <div key={cat.id}>
                                  <div className="flex items-center gap-2 px-3 py-2 text-white/50 mt-2">
                                    <ChevronDown className="w-3 h-3" />
                                    <span className="text-xs font-semibold uppercase tracking-wider">{cat.label}</span>
                                  </div>
                                  {children.map(renderLink)}
                                </div>
                              );
                            })}
                          </>
                        );
                      })() : (
                        currentLinks.filter(l => l.visible).map((link, idx) => {
                          const IconComponent = iconMap[link.icon] || FileText;
                          const isActive = idx === 0;
                          const activeColor = activeTab === 'admin'
                            ? (adminTheme?.sidebar?.activeColor || '#1E3A52')
                            : (userTheme?.sidebar?.activeColor || '#1E3A52');
                          return (
                            <div key={link.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors" style={{ backgroundColor: isActive ? activeColor : 'transparent', color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.75)' }}>
                              <IconComponent className="w-4 h-4 flex-shrink-0" />
                              <span className="text-sm font-medium">{link.label}</span>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* User Profile Footer */}
                    <div className="p-4 border-t border-white/10 mt-auto">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">
                            {activeTab === 'admin' ? 'Admin User' : 'John Doe'}
                          </p>
                          <p className="text-white/50 text-xs truncate">
                            {activeTab === 'admin' ? 'Main Admin' : 'Gold Member'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Link Summary */}
                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-3">Visible Links Summary</h4>
                  <div className="bg-gray-50 rounded-xl p-4 space-y-2 max-h-[500px] overflow-y-auto">
                    {isAdminTab ? (() => {
                      const { categories, ungrouped, grouped } = getGroupedLinks();
                      let counter = 0;
                      const renderSummaryLink = (link) => {
                        counter++;
                        const IconComponent = iconMap[link.icon] || FileText;
                        return (
                          <div key={link.id} className="flex items-center gap-3 px-3 py-2 bg-white rounded-lg border border-gray-200">
                            <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-500">{counter}</span>
                            <IconComponent className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-700 flex-1">{link.label}</span>
                            <span className="text-xs text-gray-400">{link.path}</span>
                          </div>
                        );
                      };
                      return (
                        <>
                          {ungrouped.filter(l => l.visible).map(renderSummaryLink)}
                          {categories.filter(c => c.visible).map((cat) => {
                            const children = (grouped[cat.id] || []).filter(l => l.visible);
                            return (
                              <div key={cat.id}>
                                <div className="flex items-center gap-2 px-3 py-2 mt-2">
                                  <Layers className="w-3.5 h-3.5 text-blue-500" />
                                  <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider">{cat.label}</span>
                                </div>
                                <div className="pl-2 space-y-2">
                                  {children.map(renderSummaryLink)}
                                </div>
                              </div>
                            );
                          })}
                        </>
                      );
                    })() : (
                      currentLinks.filter(l => l.visible).map((link, idx) => {
                        const IconComponent = iconMap[link.icon] || FileText;
                        return (
                          <div key={link.id} className="flex items-center gap-3 px-3 py-2 bg-white rounded-lg border border-gray-200">
                            <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-500">{idx + 1}</span>
                            <IconComponent className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-700 flex-1">{link.label}</span>
                            <span className="text-xs text-gray-400">{link.path}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>{currentLinks.filter(l => l.visible).length}</strong> visible links
                      {currentLinks.filter(l => !l.visible).length > 0 && (
                        <span className="text-blue-600 ml-1">
                          ({currentLinks.filter(l => !l.visible).length} hidden)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="px-4 py-2 text-white rounded-lg transition hover:opacity-90"
                  style={{ backgroundColor: BRAND_BLUE }}
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default AdminSidebarSettings;
